import { Platform } from 'react-native';
import { create } from 'zustand';
import type { IdentityHandle } from '../platform/identity';
import type { Storage } from '../storage-mls/index';
import type { Transport } from '../transport-mls/index';
import { logger } from '../log/log';
import { setClearSessionHandler } from '../network/api';

/**
 * Subset of `ping-openmls-sdk`'s `MessagingClient` that the @ping/ui
 * adapter layer actually relies on. Kept narrow so a minor SDK bump
 * (which adds new methods like `revokeDevice`, `buildLinkingTicket`,
 * etc.) doesn't require touching this interface — only when one of
 * THESE methods changes.
 */
export interface MessagingClient {
  close(): Promise<void>;
  onMessage(handler: (message: unknown) => void): () => void;
  // The SDK's `syncConversations` returns `IncomingMessage[]` (0.2.0+);
  // our callers ignore the return value, so `unknown[]` keeps the
  // surface stable across minor SDK bumps.
  syncConversations(): Promise<unknown[]> | Promise<void>;
}

// Static factory for `MessagingClient`. Each platform's bootstrap entry
// (apps/web, apps/desktop) imports its own SDK package statically and
// passes the class through — keeps this module platform-agnostic and
// avoids the browser-can't-resolve-bare-specifier failure that
// dynamic `import(sdkPackage)` triggered.
//
// Accepts the SDK's `cfg: ClientConfig` shape (with optional `now`)
// rather than a strict tuple so newer SDKs that add optional ClientConfig
// fields stay assignable without touching this interface.
export interface MessagingClientFactory {
  init(params: {
    identityExport: Uint8Array;
    deviceLabel: string;
    storage: Storage;
    transport: Transport;
    now?: () => number;
    /** Forwarded from `bootstrapSession`; see that interface for docs. */
    deviceSigningSecretKey?: Uint8Array;
  }): Promise<MessagingClient>;
}

export type SessionStatus =
  | { kind: 'unauthenticated' }
  | { kind: 'bootstrapping' }
  | { kind: 'authenticated'; client: MessagingClient; identity: IdentityHandle };

interface SessionState {
  status: SessionStatus;
  bootstrapPromise: Promise<void> | null;
}

const useSessionStore = create<SessionState>(() => ({
  status: { kind: 'unauthenticated' },
  bootstrapPromise: null,
}));

export { useSessionStore };

export function useSession(): {
  status: SessionStatus;
  client: MessagingClient | null;
  identity: IdentityHandle | null;
} {
  const { status } = useSessionStore();

  if (status.kind === 'authenticated') {
    return {
      status,
      client: status.client,
      identity: status.identity,
    };
  }

  return {
    status,
    client: null,
    identity: null,
  };
}

export async function bootstrapSession(params: {
  identity: IdentityHandle;
  deviceLabel: string;
  storage: Storage;
  transport: Transport;
  messagingClient: MessagingClientFactory;
  /**
   * Optional 32-byte Ed25519 secret. When supplied, threaded into
   * `MessagingClient.init({ deviceSigningSecretKey })` so the SDK's
   * `device_id` derives from this key (matching whatever device_id
   * the auth layer registered in the JWT). See
   * `ping-openmls-sdk` ClientConfig.deviceSigningSecretKey for the
   * full security rationale. Ignored on re-init when the SDK
   * already has a `LocalDevice` persisted in storage.
   */
  deviceSigningSecretKey?: Uint8Array;
}): Promise<void> {
  const currentState = useSessionStore.getState();

  if (currentState.status.kind === 'authenticated') {
    logger.info('session.bootstrap already authenticated');
    return;
  }

  if (currentState.bootstrapPromise !== null) {
    logger.debug('session.bootstrap deduplicating in-flight bootstrap');
    return currentState.bootstrapPromise;
  }

  const bootstrapPromise = (async () => {
    try {
      useSessionStore.setState({
        status: { kind: 'bootstrapping' },
        bootstrapPromise: null,
      });

      logger.info('session.bootstrap starting');

      const client = await params.messagingClient.init({
        identityExport: params.identity.identityExport,
        deviceLabel: params.deviceLabel,
        storage: params.storage,
        transport: params.transport,
        ...(params.deviceSigningSecretKey
          ? { deviceSigningSecretKey: params.deviceSigningSecretKey }
          : {}),
      });

      useSessionStore.setState({
        status: {
          kind: 'authenticated',
          client,
          identity: params.identity,
        },
        bootstrapPromise: null,
      });

      logger.info('session.bootstrap complete');
    } catch (error) {
      useSessionStore.setState({
        status: { kind: 'unauthenticated' },
        bootstrapPromise: null,
      });

      logger.error('session.bootstrap failed', error as Error);
      throw error;
    }
  })();

  useSessionStore.setState((state) => ({
    ...state,
    bootstrapPromise,
  }));

  return bootstrapPromise;
}

export async function tearDownSession(): Promise<void> {
  const currentState = useSessionStore.getState();

  if (currentState.status.kind !== 'authenticated') {
    logger.debug('session.teardown already unauthenticated');
    return;
  }

  logger.info('session.teardown starting');

  const { client } = currentState.status;

  try {
    await client.close();

    if (Platform.OS === 'web') {
      await tearDownWebWorker();
    } else {
      await releaseNativeModuleReferences();
    }

    useSessionStore.setState({
      status: { kind: 'unauthenticated' },
      bootstrapPromise: null,
    });

    logger.info('session.teardown complete');
  } catch (error) {
    logger.error('session.teardown failed', error as Error);

    useSessionStore.setState({
      status: { kind: 'unauthenticated' },
      bootstrapPromise: null,
    });

    throw error;
  }
}

// REDUNDANT BACKSTOP — not the primary teardown path.
//
// The actual WASM worker is owned by the JS SDK's `MessagingClient` and is
// terminated by `client.close()` (sdk/js `index.ts` → `this.worker.terminate()`),
// which `tearDownSession` awaits ABOVE this call. The SDK does NOT register its
// workers in `globalThis.__pingWorkers`, so in normal operation this array is
// `undefined` and the loop is a no-op.
//
// We keep the sweep purely as a safety net for any host/test that DID stash a
// worker handle there and wants it reaped on logout. Do not rely on it as the
// teardown mechanism — closing the client is what frees the WASM heap.
async function tearDownWebWorker(): Promise<void> {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    const workers = (globalThis as any).__pingWorkers as Worker[] | undefined;
    if (workers !== undefined) {
      for (const worker of workers) {
        worker.terminate();
      }
      (globalThis as any).__pingWorkers = [];
    }
  }
}

async function releaseNativeModuleReferences(): Promise<void> {
  if (Platform.OS === 'macos') {
    const nativeModule = (globalThis as any).__pingNativeModule as
      | { release?: () => void }
      | undefined;
    if (nativeModule?.release !== undefined) {
      nativeModule.release();
    }
  }
}

setClearSessionHandler(() => {
  tearDownSession().catch((error) => {
    logger.error('session.clearHandler failed', error as Error);
  });
});
